/**
 * Expandable list of quarantined records. Each record is an ExpandableSection whose header
 * summarizes the identifier columns and failed-rule badges (numbers match the Conformance Errors
 * table), and whose body shows the full failed rules and relevance-highlighted field values.
 */

import type { CSSProperties } from "react"
import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import ExpandableSection from "@cloudscape-design/components/expandable-section"
import Grid from "@cloudscape-design/components/grid"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import type { QuarantineRecord, RuleEntry, Severity } from "./types"
import CollapsibleRule from "./CollapsibleRule"
import { QUARANTINE_META_KEYS, PREFERRED_ID_COLUMNS } from "./types"

type BadgeColor = React.ComponentProps<typeof Badge>["color"]
type BoxColor = React.ComponentProps<typeof Box>["color"]

// Divider + vertical padding per record row. Cloudscape Box has no border prop, so this stays a
// plain element with an inline style.
const ROW_STYLE: CSSProperties = { borderBottom: "1px solid #e9ebed", paddingBlock: "0.25rem" }

interface FailedRecordsTableProps {
  records: QuarantineRecord[]
  /**
   * Shared rule numbering map (built by the parent from conformance errors + records).
   */
  ruleMap: Map<string, RuleEntry>
  /**
   * The run's data columns, used to validate extracted error columns.
   */
  runColumns?: string[]
}

/**
 * Splits an identifier into progressive stems for fuzzy field matching. E.g., "A_B_C" yields
 * ["A_B_C", "A_B", "A"].
 */
export function getStems(id: string): string[] {
  const stems: string[] = [id]
  const parts = id.split("_")
  for (let i = 1; i < parts.length; i++) {
    stems.push(parts.slice(0, i).join("_"))
  }
  return stems
}

/**
 * Identifies record fields that are relevant to the given failed rules. Returns fields classified
 * as "exact" (directly referenced) or "related" (stem match).
 */
export function getRelevantFields(
  rules: string[],
  recordKeys: string[],
): { exact: Set<string>; related: Set<string> } {
  const recordKeySet = new Set(recordKeys.map((k) => k.toUpperCase()))
  const ruleIdentifiers: string[] = []
  for (const rule of rules) {
    const quotedMatches = rule.matchAll(/"([A-Za-z][A-Za-z0-9_]*)"/g)
    for (const match of quotedMatches) {
      ruleIdentifiers.push(match[1].toUpperCase())
    }
    const wordMatches = rule.matchAll(/\b([A-Za-z][A-Za-z0-9_]*)\b/g)
    for (const match of wordMatches) {
      const upper = match[1].toUpperCase()
      if (recordKeySet.has(upper)) {
        ruleIdentifiers.push(upper)
      }
    }
  }
  if (ruleIdentifiers.length === 0) return { exact: new Set(), related: new Set() }

  const ruleIdSet = new Set(ruleIdentifiers)

  const ruleStems = new Set<string>()
  for (const id of ruleIdentifiers) {
    for (const stem of getStems(id)) {
      ruleStems.add(stem)
    }
  }

  const exact = new Set<string>()
  const related = new Set<string>()
  for (const key of recordKeys) {
    const upper = key.toUpperCase()
    if (ruleIdSet.has(upper)) {
      exact.add(key)
    } else {
      const keyStems = getStems(upper)
      for (const stem of keyStems) {
        if (ruleStems.has(stem)) {
          related.add(key)
          break
        }
      }
    }
  }
  return { exact, related }
}

/**
 * Extract the column names quoted in the given rules in order. Strip bracketed value lists first
 * (e.g. ColumnValues "SEX" in ["M","F"]) so codelist values are not read as columns.
 */
export function extractColumnsFromRules(rules: string[]): string[] {
  if (rules.length === 0) return []
  const cols: string[] = []
  const seen = new Set<string>()
  for (const rule of rules) {
    const withoutValueLists = rule.replace(/\[[^\]]*\]/g, "")
    for (const match of withoutValueLists.matchAll(/"([A-Za-z][A-Za-z0-9_]*)"/g)) {
      const upper = match[1].toUpperCase()
      if (!seen.has(upper)) {
        seen.add(upper)
        cols.push(match[1])
      }
    }
  }
  return cols
}

/**
 * Validate extracted columns against the run's column list Returns list of valid missing columns.
 */
export function validateMissingColumns(
  extractedColumns: string[],
  recordKeys: string[],
  runColumns: string[],
): string[] {
  if (runColumns.length === 0) return []
  const presentUpper = new Set(recordKeys.map((k) => k.toUpperCase()))
  const runByUpper = new Map(runColumns.map((c) => [c.toUpperCase(), c]))
  const result: string[] = []
  const seen = new Set<string>()
  for (const name of extractedColumns) {
    const upper = name.toUpperCase()
    const canonical = runByUpper.get(upper)
    if (canonical && !presentUpper.has(upper) && !seen.has(upper)) {
      seen.add(upper)
      result.push(canonical)
    }
  }
  return result
}

// Union of data columns across all records. Spark omits null fields from each row's JSON,
// so we collate accross all record to avoid missing columns that other rows populate.
export function collectDataKeys(records: QuarantineRecord[]): string[] {
  const keys: string[] = []
  const seen = new Set<string>()
  for (const rec of records) {
    for (const key of Object.keys(rec)) {
      if (key.startsWith("_") || QUARANTINE_META_KEYS.has(key) || seen.has(key)) continue
      seen.add(key)
      keys.push(key)
    }
  }
  return keys
}

/**
 * Returns the numbered rule entries a single quarantined record failed, taken only from its own
 * per-row `DataQualityRulesFail`. A record is never assigned a rule it did not fail: dataset-level
 * rules that carry no per-row outcome (CustomSql, Completeness, thresholded checks) are shown once
 * in the Conformance Errors table, not stamped onto every record here.
 */
export function recordRuleEntries(
  record: QuarantineRecord,
  ruleMap: Map<string, RuleEntry>,
): { num: number; severity: Severity; rule: string }[] {
  const rawRules = Array.isArray(record.DataQualityRulesFail)
    ? (record.DataQualityRulesFail as string[])
    : []
  const entries: { num: number; severity: Severity; rule: string }[] = []
  for (const rule of rawRules) {
    const entry = ruleMap.get(rule)
    if (entry) entries.push({ ...entry, rule })
  }
  entries.sort((a, b) => a.num - b.num)
  return entries
}

const severityColor = (severity: string): BadgeColor =>
  severity === "major" ? "severity-high" : "severity-medium"

function stretchHeader(node: HTMLDivElement | null) {
  if (!node) return
  const headerText = node.closest<HTMLElement>('[class*="awsui_header-text"]')
  if (headerText) {
    headerText.style.flexGrow = "1"
    headerText.style.width = "100%"
  }
  const button = node.closest<HTMLElement>('[class*="awsui_expand-button"]')
  if (button) {
    button.style.display = "flex"
    button.style.width = "100%"
  }
}

export default function FailedRecordsTable({
  records,
  ruleMap,
  runColumns = [],
}: FailedRecordsTableProps) {
  const allKeys = collectDataKeys(records)
  const idColumns = PREFERRED_ID_COLUMNS.filter((col) => allKeys.includes(col))
  if (idColumns.length === 0) {
    idColumns.push(...allKeys.slice(0, 4))
  }
  const detailKeys = allKeys.filter((k) => !idColumns.includes(k))
  const allRecordKeys = [...idColumns, ...detailKeys]

  // Shared grid so the column-label row and every record row align like a table: one cell per id
  // column plus a trailing "Failed Rules" column, each a third of the width (wraps 3 per row).
  const headerGrid = Array.from({ length: idColumns.length + 1 }, () => ({ colspan: 4 }))

  return (
    <Container header={<Header counter={`(${records.length})`}>Failed Records</Header>}>
      {/* Column labels once at the top; the left padding clears the inline expand chevron. */}
      <Box padding={{ left: "l", vertical: "xs" }}>
        <Grid gridDefinition={headerGrid}>
          {idColumns.map((col) => (
            <Box key={col} variant="awsui-key-label">
              {col}
            </Box>
          ))}
          <Box variant="awsui-key-label">FAILED RULES</Box>
        </Grid>
      </Box>
      <Box margin={{ bottom: "xs" }}>
        <hr />
      </Box>
      {records.map((rec, i) => {
        const fallbackReason = rec._failure_reason

        // Only the rules this record actually failed (its own per-row list).
        const ruleEntries = recordRuleEntries(rec, ruleMap)

        const summary = (
          <div ref={stretchHeader}>
            <Grid gridDefinition={headerGrid}>
              {idColumns.map((col) => (
                <div key={col}>{rec[col] == null ? "—" : String(rec[col] as string | number)}</div>
              ))}
              {ruleEntries.length > 0 ? (
                <SpaceBetween direction="horizontal" size="xxs">
                  {ruleEntries.map((entry) => (
                    <Badge key={entry.num} color={severityColor(entry.severity)}>
                      {entry.num}
                    </Badge>
                  ))}
                </SpaceBetween>
              ) : (
                <Box color="text-status-inactive" fontSize="body-s">
                  {fallbackReason || "—"}
                </Box>
              )}
            </Grid>
          </div>
        )

        const hasMajor = ruleEntries.some((r) => r.severity === "major")
        const { exact, related } =
          ruleEntries.length > 0
            ? getRelevantFields(
                ruleEntries.map((r) => r.rule),
                allRecordKeys,
              )
            : { exact: new Set<string>(), related: new Set<string>() }

        const visibleKeys = allRecordKeys.filter((key) => {
          const val = rec[key]
          return !((val == null || val === "") && !exact.has(key))
        })

        const failedColumns = extractColumnsFromRules(ruleEntries.map((r) => r.rule))
        const missingFlagged = validateMissingColumns(failedColumns, allRecordKeys, runColumns)

        const exactKeys = new Set<string>([...exact, ...missingFlagged])
        const displayKeys = [...visibleKeys, ...missingFlagged]

        const highlightColor = (key: string): BoxColor => {
          if (exactKeys.has(key)) return hasMajor ? "text-status-error" : "text-status-warning"
          if (related.has(key)) return "text-status-info"
          return undefined
        }

        return (
          <div key={i} style={ROW_STYLE}>
            <ExpandableSection headerText={summary}>
              <SpaceBetween size="m">
                {ruleEntries.length > 0 && (
                  <div>
                    <Box variant="awsui-key-label">Failed Rules</Box>
                    <SpaceBetween size="xs">
                      {ruleEntries.map((entry) => (
                        <Grid key={entry.num} gridDefinition={[{ colspan: 1 }, { colspan: 11 }]}>
                          <Badge color={severityColor(entry.severity)}>{entry.num}</Badge>
                          <CollapsibleRule rule={entry.rule} />
                        </Grid>
                      ))}
                    </SpaceBetween>
                  </div>
                )}
                <ColumnLayout columns={3} variant="text-grid">
                  {displayKeys.map((key) => {
                    const val = rec[key]
                    const color = highlightColor(key)
                    return (
                      <div key={key}>
                        <Box variant="awsui-key-label" color={color}>
                          {key}
                        </Box>
                        <Box color={color}>
                          {val == null || val === "" ? (
                            <Box variant="span" color="text-status-inactive">
                              EMPTY
                            </Box>
                          ) : typeof val === "object" ? (
                            JSON.stringify(val)
                          ) : (
                            String(val as string | number | boolean)
                          )}
                        </Box>
                      </div>
                    )
                  })}
                </ColumnLayout>
              </SpaceBetween>
            </ExpandableSection>
          </div>
        )
      })}
    </Container>
  )
}
